import _ from "lodash";
import Errors from "../../../utils/Errors";
import {basicContext} from "../../../utils/contextUtils";
import {deriveWorkItemStatus} from "./status";
import {applyChildrenActionRules} from "./actionRules";
import {pushActionStatusMessages, statusMessage} from "./followUp";
import {change} from "redux-form";
import {ObjectId} from "bson";
const {generateFetchFieldListAction} = require('../../../../apps/KpModule/actions/api')
const {setFieldVisibility} = require("../../../../apps/KpModule/actions");
const {getFormValues, getObjectMode, getDataListList} = require("../../../../apps/KpModule/selectors");

const toObjectID = id => new global.ObjectID(id.toString())

// Le client n'est saisi que pour une solution ; sur mesure et plateforme : fixé par le serveur
const isSolutionPart = part => _.get(part, 'product.kind.id') === 'solution'

// Statut initial des actions générées : à requalifier (« à faire ») une par une
const GENERATED_ACTION_STATUS = 'skipped'

// Objet complet d'une liste du formulaire des actions : la grille n'affiche pas une valeur réduite à son id
const fromActionDataList = (state, field, id) =>
    _.find(getDataListList(state, `m-K-workItem.Action_${field}`), {id}) || {id}

// Actions du modèle du type, proposées dans le formulaire dès que le type est choisi (création seulement).
// Les lignes déjà présentes pour un type d'action du modèle et celles ajoutées à la main sont conservées ;
// celles de l'ancien type absentes du nouveau modèle sont retirées.
const templateActionsForForm = (type, previousType, state) => {
    const formValues = getFormValues(state)
    const template = _.get(type, 'actionTemplate') || []
    const currentActions = _.get(formValues, 'actions') || []
    const templateTypeIds = template.map(actionType => actionType.id)
    const previousTypeIds = (_.get(previousType, 'actionTemplate') || []).map(actionType => actionType.id)
    const owner = _.get(formValues, 'owner')

    const generated = template.map((actionType, index) => {
        const existing = currentActions.find(action => _.get(action, 'type.id') === actionType.id)
        if (existing) return {...existing, order: index + 1}
        return {
            id: new ObjectId().toString(),
            name: actionType.name,
            type: {...fromActionDataList(state, 'type', actionType.id), name: actionType.name},
            order: index + 1,
            assignee: owner ? fromActionDataList(state, 'assignee', owner.id) : null,
            status: fromActionDataList(state, 'status', GENERATED_ACTION_STATUS),
            startDate: null,
            dueDate: null,
            description: ''
        }
    })
    const manual = currentActions.filter(action =>
        ![...templateTypeIds, ...previousTypeIds].includes(_.get(action, 'type.id'))
    )
    return [...generated, ...manual]
}

// Filet serveur : actions générées à la création si le formulaire n'en envoie aucune (ex. API)
const actionsFromTemplate = async (workItem, context) => {
    const type = await global.app.K.WorkItemType.get(
        {_id: toObjectID(workItem.type.id)},
        {...basicContext(context), fieldPath: ['actionTemplate.id', 'actionTemplate.name']}
    )
    return (_.get(type, 'actionTemplate') || []).map((actionType, index) => ({
        id: new global.ObjectID().toString(),
        name: actionType.name,
        type: {id: actionType.id},
        order: index + 1,
        assignee: workItem.owner ? {id: workItem.owner.id} : null,
        status: {id: GENERATED_ACTION_STATUS},
        startDate: null,
        dueDate: null,
        doneDate: null,
        numberOfRevisions: 0,
        description: '',
        conversations: [],
        // groupe et lien parent posés à la main : seuls les enfants envoyés par le client les reçoivent
        group: {id: context.group.id},
        workItem: {id: workItem.id}
    }))
}

// Client du work item selon le type de produit de la partie :
// sur mesure -> client du produit, plateforme -> aucun, solution -> facultatif mais déployé chez lui
const resolveClient = async (workItem, product, context) => {
    const kind = _.get(product, 'kind.id')
    if (kind === 'custom') return product.client ? {id: product.client.id} : null
    if (kind !== 'solution' || !workItem.client) return null

    const deployment = await global.app.K.Deployment.collection.findOne({
        product: toObjectID(product.id),
        client: toObjectID(workItem.client.id)
    })
    if (!deployment) throw new Errors.ValidationError(context.tc('clientWithoutDeployment'))
    return workItem.client
}

export const entity = {
    name: 'WorkItem',
    facets: ['conversations'],
    fields: [
        {path: 'code', unique: true},
        'name',
        {path: 'type', type: 'WorkItemType'},
        'Part',
        {path: 'client', type: 'GroupAndCompany', nullable: true},
        {path: 'owner', type: 'User', nullable: true},
        {path: 'priority', type: 'Influence', nullable: true},
        {path: 'dueDate', type: 'date', nullable: true},
        'description',
        {path: 'override', type: 'WorkItemOverride', nullable: true},
        {path: 'status', type: 'WorkItemStatus', nullable: true},
        {path: 'actions', type: 'Action', link: 'OTM'},
        {
            path: "sequence", unique: true, ps: {
                object: [{
                    type: "nextSequence",
                    sequenceId: "k.workItemSeq",
                    formatResult: result => _.padStart(result.toString(), 4, '0')
                }]
            }
        },
        {
            path: 'fullName',
            fieldPath: ['code', 'name'],
            f: function() {
                return `${this.code} - ${this.name}`
            }
        }
    ],
    ps: {
        context: [{
            $$u: function (context, callback) {
                if (this.options.accessType === "S" && context.restAction && context.restAction.crudType === "C") {
                    context.internalFieldPath = [
                        ...new Set([
                            ...context.internalFieldPath,
                            "sequence"
                        ])
                    ]
                }
                callback(null, context)
            }
        }]
    },
    beforeSave: async function(newObject, oldObject, context, callback) {
        try {
            const isCreation = _.get(context, 'restAction.crudType') === 'C'
            const part = await global.app.K.Part.get(
                {_id: toObjectID(newObject.part.id)},
                {...basicContext(context), fieldPath: ['product.id', 'product.code', 'product.kind', 'product.client.id']}
            )

            newObject.client = await resolveClient(newObject, part.product, context)

            if (isCreation) {
                newObject.code = `${part.product.code}-${newObject.sequence}`
                if (_.isEmpty(newObject.actions)) newObject.actions = await actionsFromTemplate(newObject, context)
            }

            // changements de statut des actions, tracés dans leur « Suivi » après l'enregistrement
            context.actionStatusChanges = await applyChildrenActionRules(newObject)

            const status = deriveWorkItemStatus(newObject.actions, newObject.override)
            if (!isCreation) {
                const stored = await global.app.K.WorkItem.collection.findOne(
                    {_id: toObjectID(newObject.id)},
                    {projection: {status: 1}}
                )
                if (stored && stored.status !== status) newObject.conversations = [
                    ...(newObject.conversations || []),
                    statusMessage(stored.status, status, 'WorkItemStatus', context)
                ]
            }
            newObject.status = {id: status}

            callback(null, newObject, oldObject)
        } catch (error) {
            callback(error)
        }
    },
    afterSave: async function(newObject, oldObject, context, callback) {
        try {
            await pushActionStatusMessages(context.actionStatusChanges, context)
            callback()
        } catch (error) {
            callback(error)
        }
    }
}

export const module_ = {
    object: 'WorkItem',
    category: {
        path: 'work',
        icon: 'check-square'
    },
    defaultSortBy: 'code',
    defaultSortDirection: 'ASC',
    viewMap: {
        dt: [
            'code',
            {path: 'part', display: 'productAndName'},
            'type',
            'name',
            {path: 'client', display: 'fullName', initiallyNotVisible: true},
            'owner',
            'priority',
            'dueDate',
            'status'
        ],
        form: {
            fields: [
                {
                    path: 'part',
                    display: 'productAndName',
                    fieldPath: ['id', 'productAndName', 'product.kind'],
                    filters: ['onlyActive'],
                    placeholder: 'sélectionner le processus / la partie du produit',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store}) => {
                            store.dispatch(setFieldVisibility('e_client', isSolutionPart(newValue)))
                            store.dispatch(
                                generateFetchFieldListAction(
                                    'm-K-workItem.WorkItem_client',
                                    store.getState,
                                    {data: {part: {id: newValue?.id}}}
                                )
                            )
                        }
                    }
                },
                {
                    path: 'type',
                    display: 'fullName',
                    fieldPath: ['id', 'fullName', 'actionTemplate.id', 'actionTemplate.name'],
                    filters: ['onlyActive'],
                    placeholder: 'sélectionner le type',
                    subscriptions: {
                        onChange: (newValue, oldValue, {store}) => {
                            const state = store.getState()
                            if (!newValue || getObjectMode(state) !== 'newObject') return
                            store.dispatch(change('editObject', 'actions', templateActionsForForm(newValue, oldValue, state)))
                        }
                    }
                },
                {path: 'code', disabled: true, required: false},
                {path: 'name', required: true},
                {
                    path: 'client',
                    display: 'fullName',
                    placeholder: 'client concerné (parmi les déploiements de la solution)',
                    filters: [
                        {
                            async: true,
                            query: async function(context, callback) {
                                try {
                                    const partId = _.get(context.data, 'part.id')
                                    if (!partId) return callback(null, {_id: null})

                                    const part = await global.app.K.Part.collection.findOne({_id: toObjectID(partId)})
                                    const product = part && await global.app.K.Product.collection.findOne({_id: part.product})
                                    if (!product) return callback(null, {_id: null})

                                    // sur mesure : le client du produit ; solution : les clients chez qui elle est déployée
                                    if (product.kind === 'custom') return callback(null, {_id: product.client})

                                    const deployments = product.kind === 'solution'
                                        ? await global.app.K.Deployment.collection.find({product: product._id}).toArray()
                                        : []
                                    callback(null, {_id: {$in: deployments.map(deployment => deployment.client)}})
                                } catch (error) {
                                    callback(error)
                                }
                            }
                        }
                    ]
                },
                {path: 'owner', placeholder: 'sélectionner le responsable'},
                'priority',
                'dueDate',
                {path: 'description', tKey: 'synthèse', type: 'richTextEditor'},
                {
                    path: 'actions',
                    defaultSortBy: 'order',
                    defaultSortDirection: 'ASC',
                    viewMap: {
                        dt: ['order', 'type', 'name', 'assignee', 'status', 'dueDate'],
                        form: [
                            {path: 'name', required: true},
                            {path: 'type', display: 'fullName'},
                            'order',
                            'assignee',
                            {path: 'status', default: {id: GENERATED_ACTION_STATUS}},
                            'startDate',
                            'dueDate',
                            {path: 'description', type: 'textarea'}
                        ]
                    }
                },
                {path: 'override', tKey: 'suspension', placeholder: 'mettre en pause ou abandonner'},
                {path: 'status', disabled: true},
                {path: 'conversations', tKey: 'suivi'}
            ],
            onOpen: ({state, store}) => {
                store.dispatch(setFieldVisibility('e_client', isSolutionPart(_.get(getFormValues(state), 'part'))))
            }
        }
    },
    filters: [
        {
            path: 'product',
            object: 'Product',
            display: 'fullName',
            client: true,
            clearable: true,
            placeholder: 'selectProduct',
            default: {id: null},
            async: true,
            query: async function(context, callback) {
                try {
                    const productId = _.get(context.data, 'product.id')
                    if (!productId) return callback(null, {})
                    const parts = await global.app.K.Part.collection
                        .find({product: toObjectID(productId)}, {projection: {_id: 1}})
                        .toArray()
                    callback(null, {part: {$in: parts.map(part => part._id)}})
                } catch (error) {
                    callback(error)
                }
            }
        },
        {
            path: 'workItemStatus',
            tKey: 'status',
            object: 'WorkItemStatus',
            display: 'name',
            client: true,
            type: 'tags',
            clearable: true,
            default: [{id: 'todo'}, {id: 'ongoing'}, {id: 'blocked'}],
            query: function(context) {
                const statuses = _.get(context.data, 'workItemStatus') || []
                return statuses.length ? {status: {$in: statuses.map(o => o.id)}} : {}
            }
        },
        {
            path: 'owner',
            object: 'User',
            display: 'firstname',
            client: true,
            clearable: true,
            placeholder: 'selectUser',
            default: {id: null},
            query: function(context) {
                const userId = _.get(context.data, 'owner.id')
                return userId ? {owner: toObjectID(userId)} : {}
            }
        }
    ]
}
